import React, { useState } from 'react'
import AsyncSelect from 'react-select/async'
import { components } from 'react-select'
import debounce from 'debounce-promise'
import { FormattedMessage } from 'react-intl'
import messages from './messages'
import VoiceTalentValueLabel from '../VoiceTalentValueLabel'
import { Button } from '@arnsbogroup/react-voicearchive'

const RatesCalculator = ({ companyCurrency = 'EUR', dealId }) => {
  const [data, setData] = useState({
    supplier: null,
    product: null,
    wordCount: 0,
    cost: 0,
    price: 0,
    priceCurrency: companyCurrency,
    costCurrency: 'EUR',
  })
  
  const [loading, setLoading] = useState(false)

  const disabled = !data.supplier || !data.product

  const onCalculate = () => {
    setLoading(true)
    fetch(`/rest/v2/supplier/${data.supplier.value}/rates/calculate`, {
      method: 'POST',
      body: JSON.stringify({
        wordCount: data.wordCount,
        productId: data. product.value,
        costCurrencyIso: data.costCurrency,
        priceCurrencyIso: data.priceCurrency,
      })
    })
    .then(response => response.json())
    .then((response) => {
      setLoading(false)
      if (response.success) {
        setData({...data, cost: response.data.cost, price: response.data.price})
      } else {
        window.alert(` ${response.errors.join(',')}`);
      }
    }).catch((error) => {
      setLoading(false)
      window.alert(`${error}`);
    })
  }

  const loadSupplierOptions = debounce((query, languageId) => {
    return new Promise((resolve, reject) => {
      let endpoint = `/rest/v2/supplier?includeRelations=1&search[identifier]=${query}&search[status]=2,4`

      if (languageId) {
        endpoint = `${endpoint}&search[language]=${languageId}`
      }

      fetch(endpoint)
        .then(response => response.json())
        .then((suppliers) => {
          resolve(suppliers.map(({ uid, userid, priorities }) => ({
            value: uid,
            label: priorities.find(({ name }) => name === 'Voice Stars') ?
            `★ ${userid}` : priorities.find(({ name }) => name === 'Custom Rates') ? `＄${userid}` : userid
          })))
        })
        .catch(reject)
    })
  }, 250)

  const loadProductOptions = debounce((query) => {
    return new Promise((resolve, reject) => {
      fetch(`/rest/v2/order_target_products?pageSize=25&orderDirection=1&search[name]=${query}&search[hidden]=0`)
        .then(response => response.json())
        .then((products) => {
          resolve(
            products.map(({ uid, name }) => ({ value: uid, label: name })),
          )
        })
        .catch(reject)
    })
  }, 250)

  return (
    <div className="w-full text-sm space-y-4">
      <div className="text-gray-500 mb-2 text-base">
        <FormattedMessage {...messages.title } />
      </div>
      <div className="p-4 border border-yellow-200 bg-yellow-50 rounded-sm text-yellow-600">
        <FormattedMessage {...messages.warning } />
      </div>

      <div className="w-full flex items-center space-x-4 text-gray-500">
        <div className="w-1/5 px-2"><FormattedMessage {...messages.supplier } /></div>
        <div className="w-1/5 px-2"><FormattedMessage {...messages.product } /></div>
        <div className="w-1/5 px-2"><FormattedMessage {...messages.wordCount } /></div>
        <div className="w-1/5 flex justify-between">
          <div className="w-1/2"><FormattedMessage {...messages.cost } /></div>
          <div className="w-1/2"><FormattedMessage {...messages.price } /></div>
        </div>
      </div>

        <div>
          <div className="w-full flex items-center space-x-4">
            <AsyncSelect
              components={{
                SingleValue: ({ children, ...props }) => (
                  <components.SingleValue {...props}>
                    <VoiceTalentValueLabel supplierId={data.supplier.value} dealId={dealId}>
                      { children }
                    </VoiceTalentValueLabel>
                  </components.SingleValue>
                ),
              }}
              value={data.supplier}
              onChange={(supplier) => setData({ ...data, supplier: supplier })}
              cacheOptions
              loadOptions={(query) => loadSupplierOptions(query, null)}
              openMenuOnFocus
              className="w-1/5"
              isClearable
            />
            <AsyncSelect
              value={data.product}
              onChange={(product) => setData({ ...data, product: product })}
              cacheOptions
              loadOptions={loadProductOptions}
              openMenuOnFocus
              defaultOptions
              className="w-1/5"
            />

            <input
              type="number"
              className="w-1/5 py-3 rounded border space-x-1 px-2 bg-white border-gray-300"
              onChange={({ target: { value } }) => setData({...data, wordCount: value})}
              value={data.wordCount}
            />

            <div className={`rounded border w-1/5 space-x-1 px-2 flex justify-between items-center bg-white border-gray-300`}>
              <input
                type="number"
                className="w-1/2 py-3"
                value={data.cost}
                disabled
              />

              <CurrencySelect
                value={data.costCurrency}
                onChange={(iso) => setData({ ...data, costCurrency: iso})}
              />

              <input
                type="number"
                className="w-1/2 py-3"
                value={data.price}
                disabled
              />

              <CurrencySelect
                value={data.priceCurrency}
                onChange={(iso) => setData({ ...data, priceCurrency: iso})}
              />

            </div>
            <Button 
              onClick={onCalculate}
              icon={['fa', 'calculator']}
              iconPosition="right"
              className="w-1/12"
              disabled={disabled || loading}
            >
              {loading ? <FormattedMessage {...messages.loading } /> : <FormattedMessage {...messages.calculate } />}
            </Button>
          </div>
        </div>
    </div>
  )
}

const CurrencySelect = ({ value, onChange, ...props }) => (
  <select value={value} onChange={({ target }) => onChange(target.value)} className="h-6 w-1/2 bg-white focus:outline-none focus:ring focus:border-blue-300" {...props}>
    { ['EUR', 'DKK', 'USD', 'GBP', 'ZAR', 'NOK', 'SEK'].map((iso) => (
      <option key={iso} value={iso}>{ iso }</option>
    )) }
  </select>
)

export default RatesCalculator
